import React, { useEffect, useState } from "react";
import { dateTime } from "@/lib/dates";
import { api } from "@/lib/api";
import { Plus, Code2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import CodeBlock from "@/components/CodeBlock";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";

const LANGS = ["javascript", "typescript", "python", "bash", "go", "rust", "html", "css", "json", "sql"];

export default function SnippetsView({ workspaceId, currentUser }) {
  const [snippets, setSnippets] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState("javascript");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");

  const load = async () => {
    if (!workspaceId) return;
    const r = await api.get("/snippets", { params: { workspace_id: workspaceId } });
    setSnippets(r.data);
  };

  useEffect(() => { load(); }, [workspaceId]);

  const create = async () => {
    if (!title.trim() || !code.trim()) return;
    try {
      await api.post("/snippets", { title, language, code, description, workspace_id: workspaceId });
      setTitle(""); setCode(""); setDescription(""); setOpen(false);
      await load();
      toast.success("Fragment salvat");
    } catch {
      toast.error("Nu a reușit");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/snippets/${id}`);
      setSnippets((prev) => prev.filter((s) => s.id !== id));
      toast.success("Șters");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nu a reușit");
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="h-14 px-6 flex items-center justify-between border-b border-line/10 bg-bg">
        <div className="flex items-center gap-2">
          <Code2 className="w-4 h-4 text-fg/50" />
          <h2 className="font-bold tracking-tight">Fragmente de cod</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-snippet-trigger" className="btn-primary flex items-center gap-1.5 text-sm">
              <Plus className="w-3.5 h-3.5" /> Fragment nou
            </button>
          </DialogTrigger>
          <DialogContent className="bg-surface border-line/10 text-fg rounded-none max-w-2xl"
            description="Publică un fragment de cod pentru echipă, cu titlu și limbaj.">
            <DialogHeader><DialogTitle className="font-bold tracking-tight">Publică un fragment de cod</DialogTitle></DialogHeader>
            <input data-testid="snippet-title-input" className="input-flat" placeholder="Titlul fragmentului" value={title} onChange={(e) => setTitle(e.target.value)} />
            <select data-testid="snippet-language-select" className="input-flat" value={language} onChange={(e) => setLanguage(e.target.value)}>
              {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <input data-testid="snippet-description-input" className="input-flat" placeholder="Descriere scurtă (opțional)" value={description} onChange={(e) => setDescription(e.target.value)} />
            <textarea
              data-testid="snippet-code-input"
              className="input-flat mono text-xs"
              placeholder="Lipește codul aici…"
              rows={10}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <DialogFooter><button data-testid="create-snippet-confirm-button" onClick={create} className="btn-primary">Salvează</button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {snippets.length === 0 && (
          <div className="text-center text-fg/40 text-sm py-12">
            <p className="label-tiny mb-2">// NICIUN FRAGMENT</p>
            <p>Salvează cod refolosibil, de care echipa are nevoie.</p>
          </div>
        )}
        {snippets.map((s) => (
          <div key={s.id} className="bg-surface border border-line/10 fade-in" data-testid={`snippet-${s.id}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-line/10">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar user={{ id: s.author_id, name: s.author_name, avatar_color: s.author_color }} size={28} />
                <div className="min-w-0">
                  <h3 className="font-semibold text-sm truncate">{s.title}</h3>
                  <p className="text-[11px] text-fg/40 mono">{s.author_name} · {dateTime(s.created_at)}</p>
                </div>
              </div>
              {s.author_id === currentUser.id && (
                <button data-testid={`delete-snippet-${s.id}-button`} onClick={() => del(s.id)} className="text-fg/30 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {s.description && <p className="px-4 py-2 text-xs text-fg/60 border-b border-line/5">{s.description}</p>}
            <div className="p-3">
              <CodeBlock lang={s.language} code={s.code} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
